/* eslint react-compiler/react-compiler: 0 */
import { useRef } from "react";
import { v4 } from "uuid";

import { useDispatch, useSelector } from "@src/store";
import {
  openDialog as _openDialog,
  closeDialog,
  DialogProps,
  VoidDialogProps,
} from "@src/store/slices/global";

type OpenDialog = {
  <K extends keyof VoidDialogProps>(dialog: K, props?: DialogProps[K]): void;
  <K extends keyof DialogProps>(dialog: K, props: DialogProps[K]): void;
};

type GetDialog = {
  <K extends keyof VoidDialogProps>(
    dialog: K
  ): {
    open: (props?: DialogProps[K]) => void;
    close: () => void;
    isOpen: boolean;
    props: DialogProps[K] | undefined;
  };
  <K extends keyof DialogProps>(
    dialog: K
  ): {
    open: (props: DialogProps[K]) => void;
    close: () => void;
    isOpen: boolean;
    props: DialogProps[K] | undefined;
  };
};

// When the dialog is closed, dialog.props returns undefined, causing
// dialog changes during the close transition. To prevent this,
// we return cached props during the close transition.
// So in this case it is fine to use the ref as it is the exact
// thing we want to prevent: rerender on the current state of the dialog.
export const useDialog = <K extends keyof DialogProps>(key: K) => {
  const { getDialog } = useDialogs();

  const propsRef = useRef<DialogProps[K] | undefined>(undefined);
  const idRef = useRef(v4());
  const dialog = getDialog(key);

  if (dialog.props !== undefined) {
    propsRef.current = dialog.props;
  }
  return { ...dialog, props: propsRef.current, id: idRef.current };
};

export const useDialogs = () => {
  const dispatch = useDispatch();
  const dialogs = useSelector((state) => state.global.dialogs);
  const isAnyDialogOpen = Object.keys(dialogs).length > 0;

  const result = {
    openDialog: (<K extends keyof DialogProps>(
      dialog: K,
      props?: DialogProps[K]
      // @ts-ignore
    ) => dispatch(_openDialog({ dialog, props }))) as OpenDialog,
    closeDialog: (dialog: keyof DialogProps) => dispatch(closeDialog(dialog)),
    isDialogOpen: (dialog: keyof DialogProps) => dialog in dialogs,
    isAnyDialogOpen,
    getDialogProps: <K extends keyof DialogProps>(dialog: K) =>
      (dialogs[dialog] ?? undefined) as DialogProps[K] | undefined,
  };
  return {
    ...result,
    getDialog: (<K extends keyof DialogProps>(dialog: K) => ({
      // @ts-ignore
      open: (props?: DialogProps[K]) => result.openDialog(dialog, props),
      close: () => result.closeDialog(dialog),
      isOpen: result.isDialogOpen(dialog),
      props: result.getDialogProps(dialog),
    })) as GetDialog,
  };
};
